import React, { useState, useEffect } from 'react';
import { 
  Database, Key, Lock, Globe, RefreshCw, 
  Save, AlertCircle, Check, X, Loader2, ChevronDown, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/Button';
import { CrmConnectionConfig, AuthMethod } from '../../lib/crm/types';
import { getCrmProvider } from '../../lib/crm/providers';
import { testCrmConnection } from '../../lib/crm/service';
import toast from 'react-hot-toast';

interface CrmConnectionFormProps {
  initialConfig: Partial<CrmConnectionConfig>;
  onSave: (config: Partial<CrmConnectionConfig>) => void;
  onCancel: () => void;
  isEditing?: boolean;
}

const CrmConnectionForm: React.FC<CrmConnectionFormProps> = ({ 
  initialConfig, 
  onSave, 
  onCancel,
  isEditing = false
}) => {
  const [config, setConfig] = useState<Partial<CrmConnectionConfig>>(initialConfig);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const provider = getCrmProvider(config.provider || '');
  
  useEffect(() => {
    // Set default values based on provider
    if (provider) {
      if (!config.url) {
        setConfig(prev => ({ ...prev, url: provider.baseUrl }));
      }
      
      if (!config.authMethod) {
        setConfig(prev => ({ 
          ...prev, 
          authMethod: provider.authMethod === 'dual' ? 'oauth2' : provider.authMethod 
        }));
      }
    }
  }, [provider]);
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!config.name) {
      newErrors.name = 'Connection name is required';
    }
    
    if (!config.url) {
      newErrors.url = 'API URL is required';
    } else if (!/^https?:\/\//.test(config.url)) {
      newErrors.url = 'API URL must start with http:// or https://';
    }
    
    // Validate auth method specific fields
    if (config.authMethod === 'oauth2') {
      if (!config.clientId) {
        newErrors.clientId = 'Client ID is required';
      }
      
      if (!config.authorizationUrl) {
        newErrors.authorizationUrl = 'Authorization URL is required';
      }
      
      if (!config.tokenUrl) {
        newErrors.tokenUrl = 'Token URL is required';
      }
      
      if (!config.redirectUri) {
        newErrors.redirectUri = 'Redirect URI is required';
      }
    } else if (config.authMethod === 'apikey') {
      if (!config.apiKeyHeader && !config.apiKeyQueryParam) {
        newErrors.apiKeyHeader = 'Either API Key Header or Query Parameter is required';
      }
    } else if (config.authMethod === 'basic') {
      if (!config.username) {
        newErrors.username = 'Username is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(config);
    }
  };
  
  const handleTestConnection = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsTesting(true);
      setTestResult(null);
      
      // In a real implementation, this would test the connection
      // For this demo, we'll simulate a test
      const status = await testCrmConnection(config as CrmConnectionConfig);
      
      if (status.status === 'connected') {
        setTestResult({ success: true, message: 'Connection test successful' });
      } else {
        setTestResult({ 
          success: false, 
          message: status.lastError?.message || 'Connection test failed' 
        });
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setTestResult({ 
        success: false, 
        message: error.message || 'Connection test failed' 
      });
    } finally {
      setIsTesting(false);
    }
  };
  
  const handleInputChange = (field: string, value: any) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    // Clear test result when form changes
    setTestResult(null);
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Connection Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={config.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`block w-full rounded-md shadow-sm sm:text-sm ${
              errors.name 
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                : 'border-gray-300 focus:border-primary focus:ring-primary'
            }`}
            placeholder="My CRM Connection"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            CRM Provider
          </label>
          <div className="flex items-center">
            {provider?.logo ? (
              <img 
                src={provider.logo} 
                alt={provider.name} 
                className="h-6 w-auto mr-2"
                onError={(e) => {
                  // Hide broken image
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <Database className="h-5 w-5 text-gray-400 mr-2" />
            )}
            <span className="text-gray-900">{provider?.name || config.provider}</span>
          </div>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          API URL <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Globe className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={config.url || ''}
            onChange={(e) => handleInputChange('url', e.target.value)}
            className={`pl-10 block w-full rounded-md shadow-sm sm:text-sm ${
              errors.url 
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                : 'border-gray-300 focus:border-primary focus:ring-primary'
            }`}
            placeholder="https://api.example.com/v1"
          />
        </div>
        {errors.url && (
          <p className="mt-1 text-sm text-red-600">{errors.url}</p>
        )}
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Authentication Method
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className={`
            flex items-center p-3 border rounded-lg cursor-pointer
            ${config.authMethod === 'oauth2' 
              ? 'border-primary bg-primary/5' 
              : 'border-gray-300 hover:border-gray-400'
            }
          `}>
            <input
              type="radio"
              name="authMethod"
              value="oauth2"
              checked={config.authMethod === 'oauth2'}
              onChange={() => handleInputChange('authMethod', 'oauth2')}
              className="sr-only"
            />
            <Lock className="h-5 w-5 text-primary mr-2" />
            <span>OAuth 2.0</span>
          </label>
          
          <label className={`
            flex items-center p-3 border rounded-lg cursor-pointer
            ${config.authMethod === 'apikey' 
              ? 'border-primary bg-primary/5' 
              : 'border-gray-300 hover:border-gray-400'
            }
          `}>
            <input
              type="radio"
              name="authMethod"
              value="apikey"
              checked={config.authMethod === 'apikey'}
              onChange={() => handleInputChange('authMethod', 'apikey')}
              className="sr-only"
            />
            <Key className="h-5 w-5 text-primary mr-2" />
            <span>API Key</span>
          </label>
          
          <label className={`
            flex items-center p-3 border rounded-lg cursor-pointer
            ${config.authMethod === 'basic' 
              ? 'border-primary bg-primary/5' 
              : 'border-gray-300 hover:border-gray-400'
            }
          `}>
            <input
              type="radio"
              name="authMethod"
              value="basic"
              checked={config.authMethod === 'basic'}
              onChange={() => handleInputChange('authMethod', 'basic')}
              className="sr-only"
            />
            <Lock className="h-5 w-5 text-primary mr-2" />
            <span>Basic Auth</span>
          </label>
        </div>
      </div>
      
      {/* OAuth 2.0 Fields */}
      {config.authMethod === 'oauth2' && (
        <div className="space-y-4 border-t border-gray-200 pt-4">
          <h3 className="text-lg font-medium text-gray-900">OAuth 2.0 Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={(config as any).clientId || ''}
                onChange={(e) => handleInputChange('clientId', e.target.value)}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  errors.clientId 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary focus:ring-primary'
                }`}
              />
              {errors.clientId && (
                <p className="mt-1 text-sm text-red-600">{errors.clientId}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Secret
              </label>
              <input
                type="password"
                value={(config as any).clientSecret || ''}
                onChange={(e) => handleInputChange('clientSecret', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Authorization URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={(config as any).authorizationUrl || ''}
              onChange={(e) => handleInputChange('authorizationUrl', e.target.value)}
              className={`block w-full rounded-md shadow-sm sm:text-sm ${
                errors.authorizationUrl 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:border-primary focus:ring-primary'
              }`}
              placeholder="https://example.com/oauth/authorize"
            />
            {errors.authorizationUrl && (
              <p className="mt-1 text-sm text-red-600">{errors.authorizationUrl}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Token URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={(config as any).tokenUrl || ''}
              onChange={(e) => handleInputChange('tokenUrl', e.target.value)}
              className={`block w-full rounded-md shadow-sm sm:text-sm ${
                errors.tokenUrl 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:border-primary focus:ring-primary'
              }`}
              placeholder="https://example.com/oauth/token"
            />
            {errors.tokenUrl && (
              <p className="mt-1 text-sm text-red-600">{errors.tokenUrl}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Redirect URI <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={(config as any).redirectUri || ''}
                onChange={(e) => handleInputChange('redirectUri', e.target.value)}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  errors.redirectUri 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary focus:ring-primary'
                }`}
                placeholder={`${window.location.origin}/auth/callback`}
              />
              {errors.redirectUri && (
                <p className="mt-1 text-sm text-red-600">{errors.redirectUri}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scope
              </label>
              <input
                type="text"
                value={(config as any).scope || ''}
                onChange={(e) => handleInputChange('scope', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                placeholder="read write"
              />
            </div>
          </div>
        </div>
      )}
      
      {/* API Key Fields */}
      {config.authMethod === 'apikey' && (
        <div className="space-y-4 border-t border-gray-200 pt-4">
          <h3 className="text-lg font-medium text-gray-900">API Key Settings</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <input
              type="password"
              value={(config as any).apiKey || ''}
              onChange={(e) => handleInputChange('apiKey', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              placeholder="Your API key"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key Header Name
              </label>
              <input
                type="text"
                value={(config as any).apiKeyHeader || ''}
                onChange={(e) => handleInputChange('apiKeyHeader', e.target.value)}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  errors.apiKeyHeader 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary focus:ring-primary'
                }`}
                placeholder="X-API-KEY"
              />
              {errors.apiKeyHeader && (
                <p className="mt-1 text-sm text-red-600">{errors.apiKeyHeader}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key Query Parameter
              </label>
              <input
                type="text"
                value={(config as any).apiKeyQueryParam || ''}
                onChange={(e) => handleInputChange('apiKeyQueryParam', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                placeholder="api_key"
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Basic Auth Fields */}
      {config.authMethod === 'basic' && (
        <div className="space-y-4 border-t border-gray-200 pt-4">
          <h3 className="text-lg font-medium text-gray-900">Basic Auth Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={(config as any).username || ''}
                onChange={(e) => handleInputChange('username', e.target.value)}
                className={`block w-full rounded-md shadow-sm sm:text-sm ${
                  errors.username 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-primary focus:ring-primary'
                }`}
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">{errors.username}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={(config as any).password || ''}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Advanced Settings */}
      <div className="border-t border-gray-200 pt-4">
        <button
          type="button"
          className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? (
            <ChevronDown className="h-4 w-4 mr-1" />
          ) : (
            <ChevronRight className="h-4 w-4 mr-1" />
          )}
          Advanced Settings
        </button>
        
        {showAdvanced && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timeout (ms)
                </label>
                <input
                  type="number"
                  min="1000"
                  max="60000"
                  step="1000"
                  value={config.timeout || 30000}
                  onChange={(e) => handleInputChange('timeout', parseInt(e.target.value))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Retry Attempts
                </label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={config.retryAttempts || 3}
                  onChange={(e) => handleInputChange('retryAttempts', parseInt(e.target.value))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Retry Delay (ms)
                </label>
                <input
                  type="number"
                  min="100"
                  max="10000"
                  step="100"
                  value={config.retryDelay || 1000}
                  onChange={(e) => handleInputChange('retryDelay', parseInt(e.target.value))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Headers
              </label>
              <textarea
                value={config.headers ? JSON.stringify(config.headers, null, 2) : ''}
                onChange={(e) => {
                  try {
                    const headers = e.target.value ? JSON.parse(e.target.value) : {};
                    handleInputChange('headers', headers);
                  } catch (error) {
                    // Invalid JSON, don't update
                  }
                }}
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm font-mono"
                placeholder='{"Content-Type": "application/json"}'
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter headers as a JSON object
              </p>
            </div>
            
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.enabled !== false}
                  onChange={(e) => handleInputChange('enabled', e.target.checked)}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm text-gray-700">Enable this connection</span>
              </label>
            </div>
          </div>
        )}
      </div>
      
      {/* Test Result */}
      {testResult && (
        <div className={`p-4 rounded-lg ${
          testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-start">
            {testResult.success ? (
              <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2" />
            )}
            <div>
              <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {testResult.success ? 'Connection Test Successful' : 'Connection Test Failed'}
              </p>
              <p className={`text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'} mt-1`}>
                {testResult.message}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Form Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancel
        </Button>
        
        <Button
          type="button"
          variant="outline"
          onClick={handleTestConnection}
          disabled={isTesting}
        >
          {isTesting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Testing...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Test Connection
            </>
          )}
        </Button>
        
        <Button
          type="submit"
        >
          <Save className="h-4 w-4 mr-2" />
          {isEditing ? 'Update Connection' : 'Save Connection'}
        </Button>
      </div>
    </form>
  );
};

export default CrmConnectionForm;